import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Switch } from "../ui/switch";
import { Save, Plus } from "lucide-react";

type Member = {
  id: string;
  name: string;
  profession: string;
  email: string;
  avatar_color: string;
  active: boolean;
  created_date: string;
};

type Team = {
  id: string;
  name: string;
  description?: string;
  leader_member_id?: string | null;
  active?: boolean;
  created_date?: string;
};

type TeamModalProps = {
  team: Team | null;
  open: boolean;
  onClose: () => void;
  onSubmit: (data: Omit<Team, "id" | "created_date">) => void;
  members: Member[];
};

export default function TeamModal({ team, open, onClose, onSubmit, members }: TeamModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const initialState = useMemo(
    () => ({
      name: team?.name ?? "",
      description: team?.description ?? "",
      leader_member_id: team?.leader_member_id ?? null,
      active: team?.active ?? true,
    }),
    [team]
  );

  const [formData, setFormData] = useState(initialState);

  useEffect(() => {
    if (open) setFormData(initialState);
  }, [open, initialState]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) return;

    setIsSubmitting(true);
    try {
      await Promise.resolve(
        onSubmit({
          name: formData.name.trim(),
          description: formData.description?.trim() || "",
          leader_member_id: formData.leader_member_id ?? null,
          active: formData.active,
        })
      );
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg bg-gradient-to-br from-white to-slate-50">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
            {team ? "✏️ Editar Equipe" : "✨ Nova Equipe"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 py-4">
          <div>
            <Label htmlFor="name" className="text-sm font-medium text-slate-700">
              Nome da equipe *
            </Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
              placeholder="Ex: DevOps, Produto, Comercial..."
              className="mt-1.5"
              autoFocus
            />
          </div>

          <div>
            <Label htmlFor="description" className="text-sm font-medium text-slate-700">
              Descrição
            </Label>
            <Input
              id="description"
              value={formData.description}
              onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))}
              placeholder="Breve descrição da equipe"
              className="mt-1.5"
            />
          </div>

          <div>
            <Label className="text-sm font-medium text-slate-700 mb-2 block">
              Líder da equipe
            </Label>
            <Select
              value={formData.leader_member_id ?? "none"}
              onValueChange={(value) =>
                setFormData((p) => ({ ...p, leader_member_id: value === "none" ? null : value }))
              }
            >
              <SelectTrigger className="bg-white">
                <SelectValue placeholder="Selecione um líder (opcional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sem líder</SelectItem>
                {members.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.name} — {m.profession}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div>
              <Label htmlFor="active" className="text-sm font-medium text-slate-700">
                Equipe Ativa
              </Label>
              <p className="text-xs text-slate-500 mt-1">
                Equipes inativas podem ser ocultadas em filtros.
              </p>
            </div>
            <Switch
              id="active"
              checked={formData.active}
              onCheckedChange={(checked) => setFormData((p) => ({ ...p, active: checked }))}
            />
          </div>

          <DialogFooter className="pt-2">
            <Button variant="outline" type="button" onClick={onClose} className="hover:scale-105 transition-all">
              Cancelar
            </Button>
            <Button
              type="submit"
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 shadow-lg shadow-purple-200 hover:shadow-xl hover:scale-105 transition-all font-semibold"
              disabled={!formData.name.trim() || isSubmitting}
            >
              {team ? <Save className="w-4 h-4 mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
              {team ? "Salvar" : "Criar Equipe"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
